<template>
  <Transition
    name="settlement-settings-modal"
    enter-active-class="settlement-settings-modal-enter-active"
    leave-active-class="settlement-settings-modal-leave-active"
    enter-from-class="settlement-settings-modal-enter-from"
    enter-to-class="settlement-settings-modal-enter-to"
    leave-from-class="settlement-settings-modal-leave-from"
    leave-to-class="settlement-settings-modal-leave-to"
  >
    <div
      v-if="isOpen"
      data-cy="settlement-settings-modal"
      class="settlement-settings-panel fixed top-[6rem] right-0 bg-white shadow-2xl z-[200] flex flex-col rounded-l-lg overflow-hidden"
    >
      <!-- ── Header ──────────────────────────────────────────── -->
      <div class="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white shrink-0">
        <h2 class="text-subtitle-1 font-weight-bold text-grey-darken-3 flex items-center gap-2">
          <v-icon size="18" color="grey-darken-1">mdi-cog</v-icon>
          Settlement Settings
        </h2>
        <LlSecondaryButton
          icon="mdi-close"
          variant="text"
          size="small"
          aria-label="Close settlement settings"
          :disabled="saving"
          data-cy="settlement-settings-modal-close"
          @click="onCancelClick"
        />
      </div>

      <!-- ── Lock / warning status bar ─────────────────────── -->
      <div
        v-if="!canReset && resetBlockedReason"
        class="flex items-center gap-1.5 px-5 py-2 bg-[rgba(var(--v-theme-error),0.08)] border-b border-[rgba(var(--v-theme-error),0.2)] text-caption text-error shrink-0"
      >
        <LlIcon icon="mdi-lock" size="14" color="error" />
        <span>{{ resetBlockedReason }}</span>
      </div>
      <div
        v-else-if="settingsLocked"
        class="flex items-center gap-1.5 px-5 py-2 bg-orange-50 border-b border-orange-100 text-caption text-orange-darken-2 shrink-0"
      >
        <v-icon size="14" color="orange-darken-2">mdi-lock</v-icon>
        <span>Settings locked &middot; frozen after statement</span>
      </div>

      <!-- ── Scrollable form body ───────────────────────────── -->
      <div class="flex-1 overflow-y-auto">

        <!-- Skeleton while matter / company data loads -->
        <SkeletonRenderer
          v-if="loading"
          :spec="skeletonSpec"
          container-class="px-5 py-5 space-y-0"
        />

        <v-form v-else ref="formRef" class="px-5 py-5 space-y-6">

          <!-- 1. Contingency Fee % -->
          <div ref="feePercentageContainer">
            <div class="flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5">
              Contingency Fee %
              <LlTooltip icon="info" text="Configured per the matter's fee agreement." />
            </div>
            <LlTextField
              :model-value="draft.feePercentage"
              type="number"
              suffix="%"
              :rules="feePercentageRules"
              :hide-details="false"
              density="compact"
              variant="outlined"
              :disabled="settingsLocked"
              @update:model-value="onFeePercentageInput"
            />
          </div>

          <!-- 2. Fee Calculation Method -->
          <div ref="feeMethodContainer">
            <div class="flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5">
              Fee Calculation Method
              <LlTooltip icon="info" text="Attorney fee is calculated on the full recovery amount." />
            </div>
            <LlSelect
              :model-value="draft.feeMethod"
              :items="feeMethodOptions"
              item-title="label"
              item-value="value"
              :item-props="(item) => ({ disabled: item.disabled })"
              :return-object="false"
              variant="outlined"
              density="compact"
              hide-details
              :disabled="settingsLocked"
              @update:model-value="draft.feeMethod = $event"
            />
            <p class="text-caption text-grey-darken-1 mt-1.5">{{ feeMethodHint }}</p>
          </div>

          <!-- 3. Expense Arrangement -->
          <div ref="expenseArrangementContainer">
            <div class="flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5">
              Expense Arrangement
              <LlTooltip icon="info" text="Determines how case expenses are treated in the settlement calculation." />
            </div>
            <LlSelect
              :model-value="draft.expenseArrangement"
              :items="expenseArrangementOptions"
              item-title="label"
              item-value="value"
              :item-props="(item) => ({ disabled: item.disabled })"
              :return-object="false"
              variant="outlined"
              density="compact"
              hide-details
              :disabled="settingsLocked"
              @update:model-value="draft.expenseArrangement = $event"
            />
            <p class="text-caption text-grey-darken-1 mt-1.5">{{ expenseArrangementHint }}</p>
          </div>

          <!-- 4. Settlement Date -->
          <div ref="settlementDateContainer">
            <div class="flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5">
              Settlement Date
              <LlTooltip icon="info" text="Shown on the settlement statement." />
            </div>
            <LlDatePicker
              :model-value="settlementDateModel"
              label=""
              :disabled="settingsLocked"
              :hide-details="true"
              data-cy="settlement-date-picker"
              @update:model-value="onSettlementDateChange"
            />
          </div>

          <!-- 5. Case Status -->
          <div ref="caseStatusContainer">
            <div class="flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5">
              Case Status
              <LlTooltip icon="info" text="Where this matter stands in the litigation lifecycle." />
            </div>
            <LlSelect
              :model-value="draftCaseStatus"
              :items="caseStatusOptions"
              item-title="label"
              item-value="label"
              :return-object="false"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              placeholder="Select Case Status"
              :disabled="settingsLocked"
              data-cy="settlement-case-status-select"
              @update:model-value="draftCaseStatus = $event"
            />
          </div>

          <!-- 6. Expense Interest Rate (read-only — redesign only; old design shows this inline on Step 2) -->
          <div v-if="redesignEnabled" ref="expenseInterestContainer">
            <div class="flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5">
              Expense Interest Rate
              <LlTooltip
                icon="info"
                :text="draftNoExpenseInterest
                  ? 'Interest has been disabled for this matter.'
                  : 'Expense interest rate configured at the firm level.'"
              />
            </div>
            <LlTextField
              :model-value="expenseInterestDisplay"
              disabled
              variant="outlined"
              density="compact"
              hide-details
            />
            <p class="text-caption text-grey-darken-1 mt-1.5">
              {{ draftNoExpenseInterest ? 'Interest is disabled for this matter.' : 'Configured at the firm level in Settings › Firm Setup › Billing.' }}
            </p>
          </div>

          <!-- 7. Expense Interest toggle — redesign only; old design shows this inline on Step 2 -->
          <div v-if="companyExpenseInterestRate">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2">
                Expense Interest
                <LlTooltip
                  icon="info"
                  :text="isFirmArrangement
                    ? 'The interest is set in Settings > Firm Setup > Billing - when enabled for the client it will show up on invoices'
                    : 'Expense interest can only be calculated for matters with the expense arrangement \'Firm advances all expenses\''"
                />
              </div>
              <LlSwitch
                :model-value="expenseInterestEnabled"
                :disabled="!isFirmArrangement"
                color="primary"
                hide-details
                @update:model-value="onExpenseInterestToggle"
              />
            </div>
          </div>

          <!-- 8. Interest As-Of Date — redesign only; shown only when interest is active -->
          <div
            v-if="redesignEnabled && companyExpenseInterestRate && isFirmArrangement && expenseInterestEnabled"
            ref="expenseInterestDateContainer"
          >
            <div class="flex items-center gap-1 text-body-2 font-weight-medium text-grey-darken-2 mb-1.5">
              Interest As-Of Date
              <LlTooltip icon="info" text="The end date for interest accrual. Expenses dated after this date accrue 0 days of interest." />
            </div>
            <LlDatePicker
              :model-value="expenseFromDateModel"
              label=""
              :hide-details="true"
              @update:model-value="onExpenseFromDateChange"
            />
            <p class="text-caption text-grey-darken-1 mt-1.5">Defaults to today's date.</p>
          </div>

        </v-form>
      </div>

      <!-- ── Panel Actions (footer) — Cancel/Save pattern per CreateChargePanel.vue ──
           (mr-16 reserves space for the support chat bubble so it doesn't cover Save) -->
      <div
        v-if="!loading"
        class="flex items-center justify-between px-5 py-4 border-t border-gray-200 bg-white shrink-0 mr-16"
      >
        <LlSecondaryButton
          data-cy="settlement-settings-modal-cancel"
          :disabled="saving"
          @click="onCancelClick"
        >
          Cancel
        </LlSecondaryButton>
        <LlSecondaryButton
          variant="flat"
          color="primary"
          data-cy="settlement-settings-modal-save"
          :disabled="settingsLocked || saving"
          :loading="saving"
          @click="onSaveClick"
        >
          Save
        </LlSecondaryButton>
      </div>
    </div>
  </Transition>

  <!-- ─── Discard Unsaved Settings Changes Dialog (SC-45732) ──
       Sibling of the panel's Transition (not nested inside `v-if="isOpen"`) so closing
       the panel and showing this dialog don't fight over the same exit transition. -->
  <LlConfirmationDialog
    v-model="showDiscardChangesDialog"
    title="Discard Changes?"
    message="You have unsaved changes to these settings. If you close now, your edits will be lost."
    confirm-text="Discard Changes"
    toggle-text="Keep Editing"
    @confirm="confirmDiscardChanges"
    @cancel="cancelDiscardChanges"
  />
</template>

<script setup lang="ts">
import { ref, reactive, computed, nextTick } from 'vue';
import type { PropType } from 'vue';
import { format, isValid, parseISO } from 'date-fns';
import { parseLocalDate, yyyymmdddash } from '@/services/utils';
import LlTextField from '@/components/LlTextField.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlDatePicker from '@/components/LlDatePicker.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlSwitch from '@/components/LlSwitch.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlIcon from '@/components/LlIcon.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import SkeletonRenderer from '@/components/SkeletonRenderer.vue';
import { FEE_METHOD, EXPENSE_ARRANGEMENT } from '@/constants/settlementConstants';
import type { FeeMethod, ExpenseArrangement } from '@/models/settlementTypes';

interface SettingsForm {
  feePercentage: number | null
  feeMethod: FeeMethod
  expenseArrangement: ExpenseArrangement
  settlementDate: string | null
}

interface SettlementSettingsSavePayload {
  settings: SettingsForm
  noExpenseInterest: boolean
  expenseFromDate: string | null
  caseStatus: string | null
}

type SkeletonItem = { type: 'paragraph' | 'list-item'; class?: string }

const SKELETON_FIELD_WIDTHS = ['w-1/2', 'w-2/3', 'w-1/2', 'w-1/3', 'w-2/5', 'w-1/3', 'w-1/3'] as const

const skeletonSpec: SkeletonItem[] = SKELETON_FIELD_WIDTHS.flatMap((width, i, arr) => [
  { type: 'paragraph', class: `mb-1 ${width}` },
  { type: 'list-item', class: i < arr.length - 1 ? 'mb-5' : undefined },
])

const props = defineProps({
  loading: { type: Boolean, default: false },
  /** Last-saved values — read once as the draft's seed each time `open()` is called.
   *  Does NOT reflect in-progress edits; the modal owns its own draft internally. */
  settingsForm: { type: Object as PropType<SettingsForm>, required: true },
  isLocked: { type: Boolean, default: false },
  canReset: { type: Boolean, default: true },
  resetBlockedReason: { type: String, default: null },
  companyExpenseInterestRate: { type: Number, default: null },
  /** Last-saved value — seed only, see `settingsForm`. */
  noExpenseInterest: { type: Boolean, default: false },
  /** Last-saved value — seed only, see `settingsForm`. YYYY-MM-DD string. */
  expenseFromDate: { type: String as () => string | null, default: null },
  /** Last-saved value — seed only, see `settingsForm`. Matter-level field (not part of
   *  the Settlement record) — same 8-value enum MatterInfo.vue's Case Status dropdown
   *  reads/writes. */
  caseStatus: { type: String as () => string | null, default: null },
  caseStatusOptions: { type: Array as PropType<Array<{ value: string; label: string }>>, default: () => [] },
  /** Gate the interest-rate / toggle / as-of-date fields (sections 5–7) behind the
   *  settlementRedesign flag. When false, interest is managed inline on Step 2. */
  redesignEnabled: { type: Boolean, default: false },
  /** True while the parent is persisting a Save — disables the footer buttons. */
  saving: { type: Boolean, default: false },
});

const emit = defineEmits<{
  save: [payload: SettlementSettingsSavePayload]
}>();

// ── Visibility + draft (SC-45774 follow-up: owned internally, not threaded through the parent) ──
const isOpen = ref(false);
const draft = reactive<SettingsForm>({
  feePercentage: null,
  feeMethod: FEE_METHOD.GROSS,
  expenseArrangement: EXPENSE_ARRANGEMENT.FIRM,
  settlementDate: null,
});
const draftNoExpenseInterest = ref(false);
const draftExpenseFromDate = ref<string | null>(null);
const draftCaseStatus = ref<string | null>(null);
const showDiscardChangesDialog = ref(false);

function seedDraft() {
  draft.feePercentage = props.settingsForm.feePercentage;
  draft.feeMethod = props.settingsForm.feeMethod;
  draft.expenseArrangement = props.settingsForm.expenseArrangement;
  draft.settlementDate = props.settingsForm.settlementDate;
  draftNoExpenseInterest.value = props.noExpenseInterest;
  draftExpenseFromDate.value = props.expenseFromDate;
  draftCaseStatus.value = props.caseStatus;
}

function isDirty(): boolean {
  return draft.feePercentage !== props.settingsForm.feePercentage
    || draft.feeMethod !== props.settingsForm.feeMethod
    || draft.expenseArrangement !== props.settingsForm.expenseArrangement
    || draft.settlementDate !== props.settingsForm.settlementDate
    || draftNoExpenseInterest.value !== props.noExpenseInterest
    || draftExpenseFromDate.value !== props.expenseFromDate
    || draftCaseStatus.value !== props.caseStatus;
}

function discardDraft() {
  isOpen.value = false;
}

function onCancelClick() {
  if (isDirty()) {
    showDiscardChangesDialog.value = true;
  } else {
    discardDraft();
  }
}

function confirmDiscardChanges() {
  showDiscardChangesDialog.value = false;
  discardDraft();
}

function cancelDiscardChanges() {
  showDiscardChangesDialog.value = false;
  // Stay open — draft untouched.
}

const formRef = ref<{ validate(): Promise<{ valid: boolean }> } | null>(null);

const settingsLocked = computed(() => props.isLocked || !props.canReset);

// ── Options ──────────────────────────────────────────────────
// 'Net of Expenses' + 'Firm absorbs expenses (from fee)' is not a supported
// combination — expenses can't be deducted from both the recovery (Net) and
// the fee (Absorbed) at once. Each side disables the option that would create it.
const feeMethodOptions = computed(() => [
  { label: 'Gross — fee calculated on full settlement amount', value: FEE_METHOD.GROSS },
  {
    label: 'Net of Expenses — fee calculated after deducting costs',
    value: FEE_METHOD.NET,
    disabled: draft.expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED,
  },
]);

const expenseArrangementOptions = computed(() => [
  { label: 'Firm advances all expenses', value: EXPENSE_ARRANGEMENT.FIRM },
  { label: 'Client pays expenses as they go', value: EXPENSE_ARRANGEMENT.CLIENT },
  {
    label: 'Firm absorbs expenses (from fee)',
    value: EXPENSE_ARRANGEMENT.ABSORBED,
    disabled: draft.feeMethod === FEE_METHOD.NET,
  },
]);

// ── Validation ───────────────────────────────────────────────
const feePercentageRules = [
  (v: number | null | string) => {
    const msg = 'Enter a number between 0 and 99 with up to 8 decimals';
    if (v == null || v === '') return true;
    const num = Number(v);
    if (Number.isNaN(num)) return msg;
    if (num < 0 || num >= 100) return msg;
    if (num !== 0 && num < 0.00000001) return msg;
    const parts = String(v).split('.');
    if (parts[1] && parts[1].length > 8) return msg;
    return true;
  },
];

async function onSaveClick() {
  const result = await formRef.value?.validate();
  if (result?.valid !== true) return;
  emit('save', {
    settings: {
      feePercentage: draft.feePercentage,
      feeMethod: draft.feeMethod,
      expenseArrangement: draft.expenseArrangement,
      settlementDate: draft.settlementDate,
    },
    noExpenseInterest: draftNoExpenseInterest.value,
    expenseFromDate: draftExpenseFromDate.value,
    caseStatus: draftCaseStatus.value,
  });
}

/** `Number('')` is `0`, not NaN — coercing blindly would silently turn a cleared
 *  field into a real 0% fee instead of the "unset" state feePercentageRules treats
 *  empty input as. */
function onFeePercentageInput(value: string | number | null) {
  draft.feePercentage = value === '' || value == null ? null : Number(value);
}

// ── Hints ────────────────────────────────────────────────────
const feeMethodHint = computed(() => {
  if (draft.expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED) {
    return "Attorney fee is calculated on the full recovery amount. 'Net of Expenses' isn't available while expenses are absorbed by the firm.";
  }
  if (draft.feeMethod === FEE_METHOD.GROSS) return 'Attorney fee is calculated on the full recovery amount.';
  if (draft.feeMethod === FEE_METHOD.NET) return 'Attorney fee is calculated after deducting costs from the recovery amount.';
  return 'Select a fee calculation method.';
});

const expenseArrangementHint = computed(() => {
  if (draft.feeMethod === FEE_METHOD.NET) {
    return "The firm fronts all costs and is reimbursed from the settlement. 'Firm absorbs expenses' isn't available with 'Net of Expenses' fee calculation.";
  }
  if (draft.expenseArrangement === EXPENSE_ARRANGEMENT.FIRM) return 'The firm fronts all costs and is reimbursed from the settlement.';
  if (draft.expenseArrangement === EXPENSE_ARRANGEMENT.ABSORBED) return 'Expenses come out of the attorney fee. The client pays no costs.';
  return 'Determines whether prior client payments appear on the settlement statement.';
});

// ── Settlement Date ──────────────────────────────────────────
const settlementDateModel = computed<Date | null>(() => {
  const dateStr = draft.settlementDate;
  if (!dateStr) return null;
  const parsed = parseISO(dateStr);
  return isValid(parsed) ? parsed : null;
});

function onSettlementDateChange(value: Date | null) {
  if (!value) return;
  const formatted = format(value, 'yyyy-MM-dd');
  if (formatted === draft.settlementDate) return;
  draft.settlementDate = formatted;
}

// ── Expense Interest Rate (read-only display) ────────────────
const expenseInterestDisplay = computed(() => {
  const rate = props.companyExpenseInterestRate;
  if (rate == null || rate === 0) return '0% — none';
  return `${rate}%`;
});

// ── Expense Interest toggle ──────────────────────────────────
// Mirrors Angular: expenseInterest = !noExpenseInterest
const expenseInterestEnabled = computed(() => !draftNoExpenseInterest.value)

const isFirmArrangement = computed(() => draft.expenseArrangement === EXPENSE_ARRANGEMENT.FIRM);

function onExpenseInterestToggle(enabled: boolean) {
  draftNoExpenseInterest.value = !enabled;
}

// ── Interest As-Of Date ──────────────────────────────────────
const expenseFromDateModel = computed<Date | null>(() => parseLocalDate(draftExpenseFromDate.value));

function onExpenseFromDateChange(val: Date | null) {
  const next = yyyymmdddash(val) || null;
  if (next === draftExpenseFromDate.value) return;
  draftExpenseFromDate.value = next;
}

// ── Field focus (used internally by open()) ───────────────────
const feePercentageContainer = ref<HTMLElement | null>(null)
const feeMethodContainer = ref<HTMLElement | null>(null)
const expenseArrangementContainer = ref<HTMLElement | null>(null)
const settlementDateContainer = ref<HTMLElement | null>(null)
const expenseInterestContainer = ref<HTMLElement | null>(null)
const expenseInterestDateContainer = ref<HTMLElement | null>(null)
const caseStatusContainer = ref<HTMLElement | null>(null)

const fieldContainers: Record<string, ReturnType<typeof ref<HTMLElement | null>>> = {
  feePercentage: feePercentageContainer,
  feeMethod: feeMethodContainer,
  expenseArrangement: expenseArrangementContainer,
  settlementDate: settlementDateContainer,
  expenseInterest: expenseInterestContainer,
  expenseInterestDate: expenseInterestDateContainer,
  caseStatus: caseStatusContainer,
}

function focusFieldInternal(key: string) {
  const el = fieldContainers[key]?.value
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  el.querySelector<HTMLElement>('input, [role="combobox"]')?.focus()
}

/** Opens the panel (seeding the draft from current props) unless it's already open, in
 *  which case an in-progress edit is left untouched. Either way, scrolls/focuses
 *  `focusField` if given. Unifies what used to be three parent-side functions
 *  (openSettingsPanel/openSettingsAndFocus/openSettingsDraft). */
function open(focusField?: string) {
  if (!isOpen.value) {
    seedDraft();
    isOpen.value = true;
  }
  if (focusField) {
    nextTick(() => focusFieldInternal(focusField));
  }
}

function close() {
  isOpen.value = false;
}

defineExpose({ open, close });
</script>

<style scoped>
.settlement-settings-panel {
  width: 380px;
  min-width: 340px;
  max-width: 420px;
  height: calc(100vh - 6rem);
}

.settlement-settings-modal-enter-active,
.settlement-settings-modal-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.settlement-settings-modal-enter-from,
.settlement-settings-modal-leave-to {
  transform: translateX(100%);
}
.settlement-settings-modal-enter-to,
.settlement-settings-modal-leave-from {
  transform: translateX(0);
}
</style>
